import {
  BarChartOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  StopOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Collapse,
  Flex,
  InputNumber,
  Popconfirm,
  Progress,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { ContextType, DataProvider } from "../contexts/DataProvider";
import {
  KeyValueDataProvider,
  useKeyValueData,
} from "../contexts/KeyValueDataProvider";
import {
  PropSimulationDataProvider,
  usePropSimulationData,
} from "../contexts/PropSimulationDataProvider";
import {
  PropStrengthDataProvider,
  usePropStrengthData,
} from "../contexts/PropStrengthDataProvider";
import { PropGraph } from "../shared/types/PropGraph";
import {
  DEFAULT_PROP_SIMULATION_CONFIG,
  PropSimulationConfig,
  PropSimulationHistory,
  PropSimulationIaaProfile,
  PropSimulationResult,
  PropSimulationRunSummary,
  PropSimulationStatRow,
  PropSimulationTypeSummary,
} from "../shared/types/PropSimulation";
import {
  createPropSimulationSession,
  getPropSimulationGameRulesText,
  getPropSimulationStrategyText,
  mergePropSimulationResults,
  normalizePropSimulationConfig,
  PROP_SIMULATION_COMMISSION_TURNS,
  propSimulationRowsToCsv,
} from "../shared/types/PropSimulationEngine";
import { dataCall } from "../tsrpc";

const { Title, Text } = Typography;

const PropGraphDataContext = createContext<ContextType<PropGraph> | null>(null);

const PROP_SIMULATION_STORAGE_VERSION = 15;
const PROP_SIMULATION_CONFIG_STORAGE_KEY = "prop-simulation-config-v1";
const PROP_SIMULATION_COLLECTIONS = {
  runs: "propsimulations",
  scopes: "propsimulation_scopes",
  propStats: "propsimulation_propstats",
};

type SimulationSnapshot = {
  running: boolean;
  saving: boolean;
  progress: number;
  completedGames: number;
  result?: PropSimulationResult;
  savedAt?: number;
  error?: string;
};

type SimulationListener = (snapshot: SimulationSnapshot) => void;

type SimulationFilterState = {
  commission: string;
  round: string;
  system: string;
  profile: string;
  type: string;
  rarity: string;
};

type SimulationView = {
  rows: PropSimulationStatRow[];
  summary?: PropSimulationRunSummary;
  typeSummaries: PropSimulationTypeSummary[];
  label: string;
};

let cachedPropSimulationConfig: PropSimulationConfig | undefined;

function loadCachedPropSimulationConfig(): PropSimulationConfig {
  if (cachedPropSimulationConfig) return cachedPropSimulationConfig;
  if (typeof window === "undefined") {
    cachedPropSimulationConfig = normalizePropSimulationConfig(DEFAULT_PROP_SIMULATION_CONFIG);
    return cachedPropSimulationConfig!;
  }
  try {
    const raw = window.localStorage.getItem(PROP_SIMULATION_CONFIG_STORAGE_KEY);
    cachedPropSimulationConfig = normalizePropSimulationConfig(
      raw ? JSON.parse(raw) : DEFAULT_PROP_SIMULATION_CONFIG,
    );
  } catch (_) {
    cachedPropSimulationConfig = normalizePropSimulationConfig(DEFAULT_PROP_SIMULATION_CONFIG);
  }
  return cachedPropSimulationConfig!;
}

function saveCachedPropSimulationConfig(config: PropSimulationConfig) {
  cachedPropSimulationConfig = normalizePropSimulationConfig(config);
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      PROP_SIMULATION_CONFIG_STORAGE_KEY,
      JSON.stringify(cachedPropSimulationConfig),
    );
  } catch (_) {
    // Ignore storage failures so private-mode or quota issues do not break the simulation page.
  }
}

const backgroundSimulation = (() => {
  let session: ReturnType<typeof createPropSimulationSession> | null = null;
  let workers: Worker[] = [];
  let workerResults = new Map<number, PropSimulationResult>();
  let workerProgress = new Map<number, number>();
  let workerStartedAt = 0;
  let timer: number | undefined;
  let running = false;
  let saving = false;
  let progress = 0;
  let completedGames = 0;
  let savedAt = 0;
  let lastNotifyAt = 0;
  let error: string | undefined;
  let result: PropSimulationResult | undefined;
  let activeConfig = normalizePropSimulationConfig(DEFAULT_PROP_SIMULATION_CONFIG);
  const listeners = new Set<SimulationListener>();

  const getSnapshot = (): SimulationSnapshot => ({
    running,
    saving,
    progress,
    completedGames,
    result,
    savedAt,
    error,
  });

  const notify = () => {
    const snapshot = getSnapshot();
    listeners.forEach((listener) => listener(snapshot));
  };

  const notifyProgress = (force = false) => {
    const now = performance.now();
    if (!force && now - lastNotifyAt < 120) return;
    lastNotifyAt = now;
    notify();
  };

  const clearTimer = () => {
    if (timer !== undefined) window.clearTimeout(timer);
    timer = undefined;
  };

  const clearWorkers = () => {
    workers.forEach((worker) => {
      try {
        worker.postMessage({ type: "stop" });
        worker.terminate();
      } catch (_) {
        // Worker may already be closed.
      }
    });
    workers = [];
    workerResults = new Map();
    workerProgress = new Map();
  };

  const getWorkerCount = (games: number) => {
    if (typeof Worker === "undefined") return 0;
    const hardwareCount = Math.max(2, Number(navigator.hardwareConcurrency || 4));
    return Math.max(1, Math.min(hardwareCount - 1, 8, Math.floor(games)));
  };

  const createWorker = () =>
    new Worker(new URL("../shared/types/PropSimulationWorker.js", import.meta.url));

  const splitWorkerConfigs = (baseConfig: PropSimulationConfig, workerCount: number) => {
    const jobs: Array<{ jobId: number; config: PropSimulationConfig }> = [];
    let offset = 0;
    const baseGames = Math.floor(baseConfig.games / workerCount);
    const remainder = baseConfig.games % workerCount;
    for (let index = 0; index < workerCount; index += 1) {
      const games = baseGames + (index < remainder ? 1 : 0);
      if (games <= 0) continue;
      jobs.push({
        jobId: index,
        config: {
          ...baseConfig,
          games,
          seed: baseConfig.seed + index * 1000003,
          gameIndexOffset: offset,
        },
      });
      offset += games;
    }
    return jobs;
  };

  const startWorkerPool = (
    input: Parameters<typeof createPropSimulationSession>[0],
    config: PropSimulationConfig,
  ) => {
    const workerCount = getWorkerCount(config.games);
    if (workerCount <= 0) return false;
    const jobs = splitWorkerConfigs(config, workerCount);
    if (!jobs.length) return false;
    try {
      clearWorkers();
      workerStartedAt = performance.now();
      workerResults = new Map();
      workerProgress = new Map(jobs.map((job) => [job.jobId, 0]));
      workers = jobs.map((job) => {
        const worker = createWorker();
        worker.onmessage = (event: MessageEvent) => {
          const payload = event.data || {};
          if (payload.type === "progress") {
            workerProgress.set(Number(payload.jobId), Number(payload.completedGames || 0));
            completedGames = Array.from(workerProgress.values()).reduce((sum, value) => sum + value, 0);
            progress = completedGames / Math.max(1, activeConfig.games);
            notifyProgress();
            return;
          }
          if (payload.type === "done") {
            workerProgress.set(Number(payload.jobId), Number(job.config.games || 0));
            workerResults.set(Number(payload.jobId), payload.result as PropSimulationResult);
            completedGames = Array.from(workerProgress.values()).reduce((sum, value) => sum + value, 0);
            progress = completedGames / Math.max(1, activeConfig.games);
            if (workerResults.size >= jobs.length) {
              workers.forEach((item) => item.terminate());
              workers = [];
              running = false;
              progress = 1;
              completedGames = activeConfig.games;
              result = mergePropSimulationResults(
                jobs.map((item) => workerResults.get(item.jobId)).filter(Boolean) as PropSimulationResult[],
                activeConfig,
                Math.round(performance.now() - workerStartedAt),
              );
              notifyProgress(true);
              if (result) void saveResult(result);
            } else {
              notifyProgress();
            }
            return;
          }
          if (payload.type === "error") {
            error = payload.error || "模拟 Worker 执行失败";
            running = false;
            clearWorkers();
            notify();
          }
        };
        worker.onerror = (event) => {
          error = event.message || "模拟 Worker 加载失败";
          running = false;
          clearWorkers();
          notify();
        };
        worker.postMessage({
          type: "start",
          jobId: job.jobId,
          input,
          config: job.config,
        });
        return worker;
      });
      return true;
    } catch (e) {
      console.error(e);
      clearWorkers();
      return false;
    }
  };

  const saveResult = async (nextResult: PropSimulationResult) => {
    saving = true;
    notify();
    try {
      /*
      const record = {
        name: `模拟推演 ${new Date().toLocaleString()}`,
        createdAt: Date.now(),
        ...nextResult,
      };
      const res = await dataCall("editor/DataInsert", {
        collection: "propsimulations",
        data: record,
      });
      if (!res.isSucc) throw res.err;
      */
      result = await saveSimulationResult(nextResult);
      savedAt = Date.now();
      error = undefined;
    } catch (e) {
      console.error(e);
      error = "模拟结果保存失败";
    } finally {
      saving = false;
      notify();
    }
  };

  const run = () => {
    if (!session) {
      running = false;
      notify();
      return;
    }
    const frame = session.runFrame(activeConfig.frameGameBudget, 8);
    progress = frame.progress;
    completedGames = frame.completedGames;
    if (frame.done) {
      clearTimer();
      running = false;
      progress = 1;
      completedGames = activeConfig.games;
      result = frame.result;
      session = null;
      notifyProgress(true);
      if (frame.result) void saveResult(frame.result);
      return;
    }
    notifyProgress();
    timer = window.setTimeout(run, 0);
  };

  return {
    start(input: Parameters<typeof createPropSimulationSession>[0], config: PropSimulationConfig) {
      clearTimer();
      clearWorkers();
      activeConfig = normalizePropSimulationConfig(config);
      session = null;
      running = true;
      saving = false;
      progress = 0;
      completedGames = 0;
      result = undefined;
      error = undefined;
      lastNotifyAt = 0;
      notify();
      if (!startWorkerPool(input, activeConfig)) {
        session = createPropSimulationSession(input, activeConfig);
        timer = window.setTimeout(run, 0);
      }
    },
    stop() {
      clearTimer();
      clearWorkers();
      session = null;
      running = false;
      notify();
    },
    subscribe(listener: SimulationListener) {
      listeners.add(listener);
      listener(getSnapshot());
      return () => {
        listeners.delete(listener);
      };
    },
  };
})();

async function saveSimulationResult(result: PropSimulationResult): Promise<PropSimulationResult> {
  const createdAt = Date.now();
  const runRecord = {
    name: `模拟推演 ${new Date(createdAt).toLocaleString()}`,
    createdAt,
    storageVersion: PROP_SIMULATION_STORAGE_VERSION,
    config: result.config,
    strategyText: result.strategyText,
    summary: result.summary,
    profileSummaries: result.profileSummaries,
    typeSummaries: result.typeSummaries,
  };
  const res = await dataCall("editor/DataInsert", {
    collection: PROP_SIMULATION_COLLECTIONS.runs,
    data: runRecord,
  });
  if (!res.isSucc) throw res.err;
  const runId = res.res.insertedIds[0];
  const scopeDocs = createSimulationScopeDocs(runId, result);
  const propStatDocs = createSimulationPropStatDocs(runId, result);
  await insertInChunks(PROP_SIMULATION_COLLECTIONS.scopes, scopeDocs, 300);
  await insertInChunks(PROP_SIMULATION_COLLECTIONS.propStats, propStatDocs, 500);
  return {
    ...result,
    _id: runId,
    storageVersion: PROP_SIMULATION_STORAGE_VERSION,
  };
}

async function loadSimulationResult(history: PropSimulationHistory): Promise<PropSimulationResult> {
  const runId = String(history._id);
  const [scopeDocs, rows] = await Promise.all([
    findAll(PROP_SIMULATION_COLLECTIONS.scopes, { runId }, { scopeTypeRank: 1, scopeKey: 1 }, 2000),
    loadSimulationScopeRows(runId, "all"),
  ]);
  const statScopes = scopeDocs
    .filter((doc: any) => doc.scopeKey !== "all")
    .map((doc: any) => ({
      key: doc.scopeKey,
      type: doc.scopeType,
      label: doc.label,
      meta: doc.meta,
      summary: doc.summary,
      rows: [],
      rowsLoaded: false,
    }));
  return {
    _id: runId,
    storageVersion: PROP_SIMULATION_STORAGE_VERSION,
    config: history.config,
    strategyText: history.strategyText,
    summary: history.summary,
    profileSummaries: history.profileSummaries,
    typeSummaries: history.typeSummaries,
    rows,
    statScopes,
  };
}

async function loadSimulationScopeRows(runId: string, scopeKey: string): Promise<PropSimulationStatRow[]> {
  const docs = await findAll(
    PROP_SIMULATION_COLLECTIONS.propStats,
    { runId, scopeKey },
    { finalValue: -1, bodyGlobalStrength: -1, globalStrength: -1 },
    5000,
  );
  return docs.map((doc: any) => normalizeSimulationStatRow(createSimulationStatRowFromDoc(doc)));
}

function createSimulationStatRowFromDoc(doc: any): PropSimulationStatRow {
  if (doc.row) return doc.row as PropSimulationStatRow;
  return {
    id: String(doc.propId ?? ""),
    name: String(doc.propName ?? ""),
    assetId: String(doc.assetId ?? ""),
    type: Number(doc.type ?? 0),
    typeName: String(doc.typeName ?? doc.type ?? ""),
    rarity: Number(doc.rarity ?? 0),
    rarityName: String(doc.rarityName ?? doc.rarity ?? ""),
    tags: Array.isArray(doc.tags) ? doc.tags : [],
    tagNames: Array.isArray(doc.tagNames) ? doc.tagNames : [],
    derived: Boolean(doc.derived),
    contribution: Number(doc.contribution ?? 0),
    globalStrength: Number(doc.globalStrength ?? 0),
    presentStrength: Number(doc.presentStrength ?? 0),
    sourceContribution: Number(doc.sourceContribution ?? doc.contribution ?? 0),
    sourceGlobalStrength: Number(doc.sourceGlobalStrength ?? doc.globalStrength ?? 0),
    sourcePresentStrength: Number(doc.sourcePresentStrength ?? doc.presentStrength ?? 0),
    intrinsicContribution: Number(doc.intrinsicContribution ?? doc.sourceContribution ?? doc.contribution ?? 0),
    intrinsicGlobalStrength: Number(doc.intrinsicGlobalStrength ?? doc.sourceGlobalStrength ?? doc.globalStrength ?? 0),
    intrinsicPresentStrength: Number(doc.intrinsicPresentStrength ?? doc.sourcePresentStrength ?? doc.presentStrength ?? 0),
    bodyContribution: Number(doc.bodyContribution ?? doc.contribution ?? 0),
    bodyGlobalStrength: Number(doc.bodyGlobalStrength ?? doc.globalStrength ?? 0),
    bodyPresentStrength: Number(doc.bodyPresentStrength ?? doc.presentStrength ?? 0),
    finalValue: Number(doc.finalValue ?? doc.intrinsicPresentStrength ?? doc.sourcePresentStrength ?? doc.presentStrength ?? 0),
    balanceValue: Number(doc.balanceValue ?? doc.finalValue ?? 0),
    actualCalculatedStrength: Number(doc.actualCalculatedStrength ?? doc.staticPrior ?? 0),
    balancedSimulationStrength: Number(doc.balancedSimulationStrength ?? doc.finalValue ?? 0),
    strengthCoefficient: doc.strengthCoefficient ?? null,
    propUpdatedAt: Number(doc.propUpdatedAt ?? 0),
    triggerValue: Number(doc.triggerValue ?? doc.intrinsicPresentStrength ?? doc.sourcePresentStrength ?? doc.presentStrength ?? 0),
    poolValue: Number(doc.poolValue ?? 0),
    synergyValue: Number(doc.synergyValue ?? 0),
    staticPrior: Number(doc.staticPrior ?? 0),
    expectedValue: Number(doc.expectedValue ?? 0),
    deviation: Number(doc.deviation ?? 0),
    deviationRate: Number(doc.deviationRate ?? 0),
    confidenceLow: Number(doc.confidenceLow ?? doc.finalValue ?? 0),
    confidenceHigh: Number(doc.confidenceHigh ?? doc.finalValue ?? 0),
    confidenceRadius: Number(doc.confidenceRadius ?? 0),
    sampleCount: Number(doc.sampleCount ?? doc.presentRounds ?? 0),
    effectiveSampleCount: Number(doc.effectiveSampleCount ?? doc.presentRounds ?? 0),
    components: doc.components ?? {},
    presentRounds: Number(doc.presentRounds ?? 0),
    uses: Number(doc.uses ?? 0),
    effectEvents: Number(doc.effectEvents ?? 0),
  };
}

function normalizeSimulationStatRow(row: PropSimulationStatRow): PropSimulationStatRow {
  const sourcePresent = row.sourcePresentStrength ?? row.presentStrength ?? 0;
  const intrinsicPresent = row.intrinsicPresentStrength ?? sourcePresent;
  const finalValue = row.finalValue ?? intrinsicPresent;
  const expectedValue = row.expectedValue ?? 0;
  const deviation = row.deviation ?? finalValue - expectedValue;
  return {
    ...row,
    sourceContribution: row.sourceContribution ?? row.contribution,
    sourceGlobalStrength: row.sourceGlobalStrength ?? row.globalStrength,
    sourcePresentStrength: row.sourcePresentStrength ?? row.presentStrength,
    intrinsicContribution: row.intrinsicContribution ?? row.sourceContribution ?? row.contribution,
    intrinsicGlobalStrength: row.intrinsicGlobalStrength ?? row.sourceGlobalStrength ?? row.globalStrength,
    intrinsicPresentStrength: intrinsicPresent,
    bodyContribution: row.bodyContribution ?? row.contribution,
    bodyGlobalStrength: row.bodyGlobalStrength ?? row.globalStrength,
    bodyPresentStrength: row.bodyPresentStrength ?? row.presentStrength,
    finalValue,
    balanceValue: row.balanceValue ?? finalValue,
    actualCalculatedStrength: row.actualCalculatedStrength ?? row.staticPrior ?? finalValue,
    balancedSimulationStrength: row.balancedSimulationStrength ?? finalValue,
    strengthCoefficient: row.strengthCoefficient ?? null,
    propUpdatedAt: row.propUpdatedAt ?? 0,
    triggerValue: row.triggerValue ?? intrinsicPresent,
    poolValue: row.poolValue ?? 0,
    synergyValue: row.synergyValue ?? 0,
    staticPrior: row.staticPrior ?? finalValue,
    expectedValue,
    deviation,
    deviationRate: row.deviationRate ?? (expectedValue ? deviation / expectedValue * 100 : 0),
    confidenceLow: row.confidenceLow ?? finalValue,
    confidenceHigh: row.confidenceHigh ?? finalValue,
    confidenceRadius: row.confidenceRadius ?? 0,
    sampleCount: row.sampleCount ?? row.presentRounds ?? 0,
    effectiveSampleCount: row.effectiveSampleCount ?? row.presentRounds ?? 0,
    components: row.components ?? {},
  };
}

async function deleteSimulationResult(runId: string) {
  await deleteSimulationRunsByIds([runId]);
}

async function deleteSimulationRunsByIds(runIds: string[]) {
  const ids = Array.from(new Set(runIds.map(String).filter(Boolean)));
  if (!ids.length) return;
  await Promise.all([
    dataCall("editor/DataDelete", {
      collection: PROP_SIMULATION_COLLECTIONS.propStats,
      filter: { runId: { $in: ids } },
      multi: true,
    }),
    dataCall("editor/DataDelete", {
      collection: PROP_SIMULATION_COLLECTIONS.scopes,
      filter: { runId: { $in: ids } },
      multi: true,
    }),
  ]);
  const res = await dataCall("editor/DataDelete", {
    collection: PROP_SIMULATION_COLLECTIONS.runs,
    filter: { _id: { $in: ids } },
    multi: true,
  });
  if (!res.isSucc) throw res.err;
}

async function clearLegacySimulationData() {
  const legacyRuns = await dataCall("editor/DataFind", {
    collection: PROP_SIMULATION_COLLECTIONS.runs,
    filter: { storageVersion: { $ne: PROP_SIMULATION_STORAGE_VERSION } },
    limit: 100000,
    projection: { _id: 1 },
  });
  if (!legacyRuns.isSucc) return;
  await deleteSimulationRunsByIds(legacyRuns.res.data.map((item: any) => String(item._id)));
}

function createSimulationScopeDocs(runId: string, result: PropSimulationResult) {
  return [
    {
      runId,
      scopeKey: "all",
      scopeType: "all",
      scopeTypeRank: getScopeTypeRank("all"),
      label: "全部数据",
      meta: {},
      summary: result.summary,
      typeSummaries: result.typeSummaries,
      rowCount: result.rows.length,
    },
    ...(result.statScopes ?? [])
      .filter((scope) => scope.key !== "all")
      .map((scope) => ({
        runId,
        scopeKey: scope.key,
        scopeType: scope.type,
        scopeTypeRank: getScopeTypeRank(scope.type),
        label: scope.label,
        meta: scope.meta ?? {},
        summary: scope.summary,
        typeSummaries: buildTypeSummariesFromRows(scope.rows ?? []),
        rowCount: scope.rows?.length ?? 0,
      })),
  ];
}

function createSimulationPropStatDocs(runId: string, result: PropSimulationResult) {
  const docs: any[] = [];
  const pushRows = (scopeKey: string, scopeType: string, meta: any, rows: PropSimulationStatRow[]) => {
    rows.forEach((row) => {
      if (!shouldPersistSimulationStatRow(scopeType, row)) return;
      docs.push({
        runId,
        scopeKey,
        scopeType,
        commissionIndex: meta?.commissionIndex ?? 0,
        roundIndex: meta?.roundIndex ?? 0,
        profileKey: meta?.profileKey ?? "",
        systemTag: meta?.systemTag ?? 0,
        propId: row.id,
        propName: row.name,
        assetId: row.assetId,
        type: row.type,
        typeName: row.typeName,
        rarity: row.rarity,
        rarityName: row.rarityName,
        tags: row.tags,
        tagNames: row.tagNames,
        derived: row.derived,
        contribution: row.contribution,
        globalStrength: row.globalStrength,
        presentStrength: row.presentStrength,
        sourceContribution: row.sourceContribution ?? row.contribution,
        sourceGlobalStrength: row.sourceGlobalStrength ?? row.globalStrength,
        sourcePresentStrength: row.sourcePresentStrength ?? row.presentStrength,
        intrinsicContribution: row.intrinsicContribution ?? row.sourceContribution ?? row.contribution,
        intrinsicGlobalStrength: row.intrinsicGlobalStrength ?? row.sourceGlobalStrength ?? row.globalStrength,
        intrinsicPresentStrength: row.intrinsicPresentStrength ?? row.sourcePresentStrength ?? row.presentStrength,
        bodyContribution: row.bodyContribution ?? row.contribution,
        bodyGlobalStrength: row.bodyGlobalStrength ?? row.globalStrength,
        bodyPresentStrength: row.bodyPresentStrength ?? row.presentStrength,
        finalValue: row.finalValue ?? row.intrinsicPresentStrength ?? row.sourcePresentStrength ?? row.presentStrength,
        balanceValue: row.balanceValue ?? row.finalValue ?? row.intrinsicPresentStrength ?? row.sourcePresentStrength ?? row.presentStrength,
        actualCalculatedStrength: row.actualCalculatedStrength ?? row.staticPrior ?? 0,
        balancedSimulationStrength: row.balancedSimulationStrength ?? row.finalValue ?? row.intrinsicPresentStrength ?? row.sourcePresentStrength ?? row.presentStrength ?? 0,
        strengthCoefficient: row.strengthCoefficient ?? null,
        propUpdatedAt: row.propUpdatedAt ?? 0,
        triggerValue: row.triggerValue ?? row.intrinsicPresentStrength ?? row.sourcePresentStrength ?? row.presentStrength,
        poolValue: row.poolValue ?? 0,
        synergyValue: row.synergyValue ?? 0,
        staticPrior: row.staticPrior ?? 0,
        expectedValue: row.expectedValue ?? 0,
        deviation: row.deviation ?? 0,
        deviationRate: row.deviationRate ?? 0,
        confidenceLow: row.confidenceLow ?? row.finalValue ?? row.intrinsicPresentStrength ?? row.sourcePresentStrength ?? row.presentStrength ?? 0,
        confidenceHigh: row.confidenceHigh ?? row.finalValue ?? row.intrinsicPresentStrength ?? row.sourcePresentStrength ?? row.presentStrength ?? 0,
        confidenceRadius: row.confidenceRadius ?? 0,
        sampleCount: row.sampleCount ?? row.presentRounds,
        effectiveSampleCount: row.effectiveSampleCount ?? row.presentRounds,
        components: row.components ?? {},
        presentRounds: row.presentRounds,
        uses: row.uses,
        effectEvents: row.effectEvents ?? 0,
      });
    });
  };
  pushRows("all", "all", {}, result.rows);
  (result.statScopes ?? []).filter((scope) => scope.key !== "all").forEach((scope) => {
    pushRows(scope.key, scope.type, scope.meta ?? {}, scope.rows ?? []);
  });
  return docs;
}

function shouldPersistSimulationStatRow(scopeType: string, row: PropSimulationStatRow) {
  if (scopeType !== "filter") return true;
  return Number(row.presentRounds || 0) > 0 ||
    Number(row.uses || 0) > 0 ||
    Number(row.effectEvents || 0) > 0 ||
    Number(row.contribution || 0) !== 0 ||
    Number(row.intrinsicContribution || 0) !== 0 ||
    Number(row.bodyContribution || 0) !== 0 ||
    Number(row.sourceContribution || 0) !== 0;
}

async function insertInChunks(collection: string, docs: any[], chunkSize: number) {
  for (let index = 0; index < docs.length; index += chunkSize) {
    const chunk = docs.slice(index, index + chunkSize);
    if (!chunk.length) continue;
    const res = await dataCall("editor/DataInsert", {
      collection,
      data: chunk,
    });
    if (!res.isSucc) throw res.err;
  }
}

async function findAll(
  collection: string,
  filter: Record<string, any>,
  sort: Record<string, any>,
  limit: number,
) {
  const all: any[] = [];
  let skip = 0;
  const pageSize = Math.min(5000, Math.max(1, limit));
  while (all.length < limit) {
    const res = await dataCall("editor/DataFind", {
      collection,
      filter,
      sort,
      skip,
      limit: Math.min(pageSize, limit - all.length),
    });
    if (!res.isSucc) throw res.err;
    all.push(...res.res.data);
    if (res.res.data.length < pageSize) break;
    skip += res.res.data.length;
  }
  return all;
}

function getScopeTypeRank(type: string) {
  return ({ all: 0, commission: 1, round: 2, system: 3, profile: 4, filter: 5 } as Record<string, number>)[type] ?? 99;
}

const PageWrap = styled.div`
  height: 100%;
  padding: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (max-width: 900px) {
    padding: 8px;
    overflow-y: auto;
    overscroll-behavior: contain;
  }
`;

const MainGrid = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(420px, 520px) minmax(0, 1fr);
  gap: 10px;

  @media (max-width: 1180px) {
    grid-template-columns: minmax(350px, 430px) minmax(0, 1fr);
  }

  @media (max-width: 900px) {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    overflow: visible;
  }
`;

const SidePanel = styled.div`
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (max-width: 900px) {
    overflow: visible;
  }
`;

const ResultPanel = styled.div`
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-right: 4px;

  @media (max-width: 900px) {
    overflow: visible;
    padding-right: 0;
  }
`;

const CompactCard = styled(Card)`
  .ant-card-body {
    padding: 12px;
  }
`;

const FilterBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 20;
  padding: 8px 10px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);

  @media (max-width: 600px) {
    top: -1px;
    padding: 8px;

    .ant-space,
    .ant-select,
    .ant-input,
    .ant-input-affix-wrapper {
      max-width: 100%;
    }
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;

  .ant-card-body {
    padding: 10px;
  }

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const ConfigRow = styled.div`
  display: grid;
  grid-template-columns: 150px minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
    align-items: stretch;
    gap: 4px;
  }
`;

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 96px;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;

  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const ChartGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;

  @media (max-width: 1180px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const BarChartWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BarRow = styled.div`
  display: grid;
  grid-template-columns: 128px minmax(0, 1fr) 72px;
  gap: 8px;
  align-items: center;
  font-size: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 92px minmax(0, 1fr) 58px;
    gap: 6px;
  }
`;

const BarLabel = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const BarTrack = styled.div`
  height: 8px;
  border-radius: 999px;
  background: #eef2f7;
  overflow: hidden;
`;

const BarFill = styled.div<{ $width: number; $color: string }>`
  width: ${(props) => props.$width}%;
  height: 100%;
  background: ${(props) => props.$color};
`;

const TableCard = styled(CompactCard)`
  flex: 1 1 420px;
  min-height: 0;

  .ant-card-body {
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .ant-table-wrapper {
    flex: 1 1 auto;
    min-height: 0;
  }

  @media (max-width: 900px) {
    flex-basis: auto;
    min-height: 520px;

    .ant-table-content,
    .ant-table-body {
      overflow-x: auto !important;
    }
  }
`;

export default function PagePropSimulation() {
  return (
    <KeyValueDataProvider>
      <PropStrengthDataProvider>
        <DataProvider<PropGraph>
          context={PropGraphDataContext}
          collection="propgraphs"
          sort={{ _id: 1 }}
          limit={10000}
        >
          <PropSimulationDataProvider>
            <PagePropSimulationInner />
          </PropSimulationDataProvider>
        </DataProvider>
      </PropStrengthDataProvider>
    </KeyValueDataProvider>
  );
}

function PagePropSimulationInner() {
  const propContext = usePropGraphData();
  const historyContext = usePropSimulationData();
  const strengthContext = usePropStrengthData();
  const tagContext = useKeyValueData("proptags");
  const typeContext = useKeyValueData("proptypes");
  const rarityContext = useKeyValueData("proprarities");
  const savedAtRef = useRef(0);
  const errorRef = useRef<string | undefined>();

  const [config, setConfig] = useState<PropSimulationConfig>(() => loadCachedPropSimulationConfig());
  const [props, setProps] = useState<PropGraph[]>([]);
  const [tags, setTags] = useState<any[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [rarities, setRarities] = useState<any[]>([]);
  const [strengths, setStrengths] = useState<any[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [completedGames, setCompletedGames] = useState(0);
  const [result, setResult] = useState<PropSimulationResult | undefined>();
  const [loadingScopeRows, setLoadingScopeRows] = useState(false);
  const [filters, setFilters] = useState<SimulationFilterState>({
    commission: "all",
    round: "all",
    system: "all",
    profile: "all",
    type: "all",
    rarity: "all",
  });

  useEffect(() => {
    loadAllData();
    initializeSimulationHistory();
    const unsubscribe = backgroundSimulation.subscribe((snapshot) => {
      setRunning(snapshot.running);
      setProgress(snapshot.progress);
      setCompletedGames(snapshot.completedGames);
      if (snapshot.result) setResult(snapshot.result);
      if (snapshot.savedAt && snapshot.savedAt !== savedAtRef.current) {
        savedAtRef.current = snapshot.savedAt;
        loadSimulationHistory();
      }
      if (snapshot.error && snapshot.error !== errorRef.current) {
        errorRef.current = snapshot.error;
        message.error(snapshot.error);
      }
    });
    return unsubscribe;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    saveCachedPropSimulationConfig(config);
  }, [config]);

  const initializeSimulationHistory = async () => {
    try {
      await clearLegacySimulationData();
    } finally {
      await loadSimulationHistory();
    }
  };

  const loadSimulationHistory = () => {
    return historyContext.load({ storageVersion: PROP_SIMULATION_STORAGE_VERSION }, true);
  };

  const strategyText = useMemo(
    () => getPropSimulationStrategyText(config),
    [config],
  );
  const gameRulesText = useMemo(
    () => getPropSimulationGameRulesText(),
    [],
  );

  const normalizedConfig = useMemo(
    () => normalizePropSimulationConfig(config),
    [config],
  );

  const filterOptions = useMemo(
    () => buildSimulationFilterOptions(
      result,
      filters.commission,
      tags,
      types,
      rarities,
      result?.config.iaaProfiles ?? normalizedConfig.iaaProfiles,
    ),
    [result, filters.commission, tags, types, rarities, normalizedConfig.iaaProfiles],
  );

  useEffect(() => {
    if (!filterOptions.roundOptions.some((option) => option.value === filters.round)) {
      setFilters((prev) => ({ ...prev, round: "all" }));
    }
  }, [filterOptions.roundOptions, filters.round]);

  const simulationView = useMemo(
    () => buildSimulationView(
      result,
      filters,
      tags,
      types,
      rarities,
      result?.config.iaaProfiles ?? normalizedConfig.iaaProfiles,
    ),
    [result, filters, tags, types, rarities, normalizedConfig.iaaProfiles],
  );

  const selectedScopeKey = useMemo(() => {
    if (!result) return "";
    return findSimulationScope(result, filters)?.key ?? "all";
  }, [result, filters]);

  useEffect(() => {
    if (!result?._id || !selectedScopeKey || selectedScopeKey === "all") return;
    const scope = result.statScopes?.find((item) => item.key === selectedScopeKey) as
      | (NonNullable<PropSimulationResult["statScopes"]>[number] & { rowsLoaded?: boolean })
      | undefined;
    if (!scope || scope.rowsLoaded) return;
    let canceled = false;
    setLoadingScopeRows(true);
    loadSimulationScopeRows(String(result._id), selectedScopeKey)
      .then((rows) => {
        if (canceled) return;
        setResult((current) => {
          if (!current || String(current._id) !== String(result._id)) return current;
          return {
            ...current,
            statScopes: (current.statScopes ?? []).map((item) =>
              item.key === selectedScopeKey
                ? { ...item, rows, rowsLoaded: true } as any
                : item,
            ),
          };
        });
      })
      .catch((error) => {
        if (!canceled) message.error(error?.message ?? "加载模拟统计失败");
      })
      .finally(() => {
        if (!canceled) setLoadingScopeRows(false);
      });
    return () => {
      canceled = true;
    };
  }, [result?._id, selectedScopeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAllData = async () => {
    setLoadingData(true);
    try {
      const [nextProps, nextTags, nextTypes, nextRarities, nextStrengths] =
        await Promise.all([
          propContext.get({}, { _id: 1 }, 10000),
          tagContext.get({}, { value: 1 }, 10000),
          typeContext.get({}, { value: 1 }, 10000),
          rarityContext.get({}, { value: 1 }, 10000),
          strengthContext.get({}, { rarity: 1 }, 1000),
        ]);
      setProps(nextProps);
      setTags(nextTags);
      setTypes(nextTypes);
      setRarities(nextRarities);
      setStrengths(nextStrengths);
      message.success("模拟数据已刷新");
    } catch (error) {
      console.error(error);
      message.error("加载模拟数据失败");
    } finally {
      setLoadingData(false);
    }
  };

  const startSimulation = () => {
    if (!props.length) {
      message.warning("请先加载道具数据");
      return;
    }
    const seed = createRandomSeed();
    const nextConfig = normalizePropSimulationConfig({ ...config, seed });
    setConfig((prev) => ({ ...prev, seed }));
    backgroundSimulation.start(
      { props, tags, types, rarities, strengths },
      nextConfig,
    );
  };

  const stopSimulation = () => {
    backgroundSimulation.stop();
  };

  const updateProfilePercent = (
    profile: PropSimulationIaaProfile,
    percent: number | null,
  ) => {
    setConfig((prev) => ({
      ...prev,
      iaaProfiles: prev.iaaProfiles.map((item) =>
        item.key === profile.key
          ? { ...item, percent: Math.max(0, Number(percent ?? 0)) }
          : item,
      ),
    }));
  };

  const exportCsv = (targetResult = result, rows?: PropSimulationStatRow[]) => {
    if (!targetResult) {
      message.warning("没有可导出的统计数据");
      return;
    }
    const blob = new Blob([propSimulationRowsToCsv(rows ?? targetResult.rows)], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `prop-simulation-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <PageWrap>
      <Flex justify="space-between" align="center">
        <Space>
          <BarChartOutlined />
          <Title level={5} style={{ margin: 0 }}>模拟推演</Title>
          <Text type="secondary">浏览器异步模拟，结果保存到 propsimulations</Text>
        </Space>
        <Space>
          <Button icon={<ReloadOutlined />} loading={loadingData} onClick={loadAllData}>
            刷新数据
          </Button>
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            loading={running}
            disabled={loadingData || running}
            onClick={startSimulation}
          >
            开始模拟
          </Button>
          <Button
            danger
            icon={<StopOutlined />}
            disabled={!running}
            onClick={stopSimulation}
          >
            停止
          </Button>
        </Space>
      </Flex>

      <MainGrid>
        <SidePanel>
          <CompactCard title="模拟参数">
            <ConfigRow>
              <Text>模拟总局数</Text>
              <InputNumber
                min={1}
                max={100000000}
                precision={0}
                value={config.games}
                style={{ width: "100%" }}
                onChange={(value) =>
                  setConfig((prev) => ({ ...prev, games: Number(value ?? 1) }))
                }
              />
            </ConfigRow>
            <ConfigRow>
              <Text>每帧模拟局数</Text>
              <InputNumber
                min={1}
                max={1000}
                precision={0}
                value={config.frameGameBudget}
                style={{ width: "100%" }}
                onChange={(value) =>
                  setConfig((prev) => ({
                    ...prev,
                    frameGameBudget: Number(value ?? 1),
                  }))
                }
              />
            </ConfigRow>
            <Text strong>付费分布</Text>
            <div style={{ marginTop: 8 }}>
              {config.iaaProfiles.map((profile) => (
                <ProfileGrid key={profile.key}>
                  <div>
                    <Text>{profile.name}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {profile.description}
                    </Text>
                  </div>
                  <InputNumber
                    min={0}
                    max={100}
                    precision={0}
                    value={profile.percent}
                    addonAfter="%"
                    onChange={(value) => updateProfilePercent(profile, value)}
                  />
                </ProfileGrid>
              ))}
            </div>
            <Alert
              type="info"
              showIcon
              message={`当前比例合计：${normalizedConfig.iaaProfiles.reduce(
                (sum: number, item: PropSimulationIaaProfile) => sum + item.percent,
                0,
              )}%`}
            />
          </CompactCard>

          <CompactCard title="模拟进度">
            <Progress
              percent={Math.round(progress * 10000) / 100}
              status={running ? "active" : result ? "success" : "normal"}
            />
            <Text type="secondary">
              {completedGames.toLocaleString()} / {normalizedConfig.games.toLocaleString()} 局
            </Text>
          </CompactCard>

          <CompactCard title="策略">
            <Collapse
              size="small"
              bordered={false}
              defaultActiveKey={[]}
              items={[
                {
                  key: "rules",
                  label: "查看完整游戏规则",
                  children: (
                    <ol style={{ margin: 0, paddingLeft: 18 }}>
                      {gameRulesText.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ol>
                  ),
                },
                {
                  key: "strategy",
                  label: "查看行为树决策规则",
                  children: (
                    <ol style={{ margin: 0, paddingLeft: 18 }}>
                      {strategyText.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ol>
                  ),
                },
              ]}
            />
          </CompactCard>

          <HistoryTable
            histories={historyContext.data}
            onView={async (history) => setResult(await loadSimulationResult(history))}
            onDelete={async (history) => {
              await deleteSimulationResult(history._id);
              await loadSimulationHistory();
              if ((result as PropSimulationHistory | undefined)?._id === history._id) {
                setResult(undefined);
              }
            }}
            onExport={async (history) => exportCsv(await loadSimulationResult(history))}
          />
        </SidePanel>

        <ResultPanel>
          <SimulationFilters
            disabled={!result}
            filters={filters}
            options={filterOptions}
            onChange={setFilters}
          />
          <SummaryCards view={simulationView} />
          <Charts view={simulationView} />
          <StatsTable
            result={result}
            view={simulationView}
            loading={loadingScopeRows}
            onExport={() => exportCsv(result, simulationView.rows)}
          />
        </ResultPanel>
      </MainGrid>
    </PageWrap>
  );
}

function SimulationFilters({
  disabled,
  filters,
  options,
  onChange,
}: {
  disabled: boolean;
  filters: SimulationFilterState;
  options: ReturnType<typeof buildSimulationFilterOptions>;
  onChange: (filters: SimulationFilterState) => void;
}) {
  return (
    <FilterBar>
      <Flex gap={8} wrap="wrap" align="center">
        <Text strong>筛选</Text>
        <Select
          disabled={disabled}
          value={filters.commission}
          options={options.commissionOptions}
          onChange={(commission) => onChange({ ...filters, commission, round: "all" })}
          style={{ width: 140 }}
        />
        <Select
          disabled={disabled}
          value={filters.round}
          options={options.roundOptions}
          onChange={(round) => onChange({ ...filters, round })}
          style={{ width: 140 }}
        />
        <Select
          disabled={disabled}
          value={filters.system}
          options={options.systemOptions}
          onChange={(system) => onChange({ ...filters, system })}
          style={{ width: 160 }}
        />
        <Select
          disabled={disabled}
          value={filters.profile}
          options={options.profileOptions}
          onChange={(profile) => onChange({ ...filters, profile })}
          style={{ width: 190 }}
        />
        <Select
          disabled={disabled}
          value={filters.type}
          options={options.typeOptions}
          onChange={(type) => onChange({ ...filters, type })}
          style={{ width: 130 }}
        />
        <Select
          disabled={disabled}
          value={filters.rarity}
          options={options.rarityOptions}
          onChange={(rarity) => onChange({ ...filters, rarity })}
          style={{ width: 130 }}
        />
      </Flex>
    </FilterBar>
  );
}

function SummaryCards({ view }: { view: SimulationView }) {
  const summary = view.summary;
  const slowestStage = summary?.performance?.[0];
  return (
    <SummaryGrid>
      <CompactCard>
        <Statistic title="当前范围" value={view.label} />
      </CompactCard>
      <CompactCard>
        <Statistic title="总魔力" value={summary?.totalMagic ?? 0} precision={2} />
      </CompactCard>
      <CompactCard>
        <Statistic title="平均每局" value={summary?.averageMagicPerGame ?? 0} precision={2} />
      </CompactCard>
      <CompactCard>
        <Statistic title="平均每轮" value={summary?.averageMagicPerTurn ?? 0} precision={4} />
      </CompactCard>
      <CompactCard>
        <Statistic title="样本轮数" value={summary?.totalTurns ?? 0} />
      </CompactCard>
      <CompactCard>
        <Statistic title="登场道具" value={summary?.presentPropCount ?? 0} suffix={`/ ${summary?.propCount ?? 0}`} />
      </CompactCard>
      <CompactCard>
        <Statistic title="平均材料库" value={summary?.averageLibrarySize ?? 0} precision={2} />
      </CompactCard>
      <CompactCard>
        <Statistic title="材料库峰值" value={summary?.maxLibrarySize ?? 0} />
      </CompactCard>
      <CompactCard>
        <Statistic title="精简原料" value={summary?.prunedMaterialCount ?? 0} />
      </CompactCard>
      <CompactCard>
        <Statistic
          title="模拟瓶颈"
          value={slowestStage ? `${slowestStage.label} ${slowestStage.percent}%` : "暂无"}
        />
      </CompactCard>
    </SummaryGrid>
  );
}

function Charts({ view }: { view: SimulationView }) {
  const topRows = view.rows
    .slice()
    .sort((a, b) => Number(b.finalValue ?? b.intrinsicPresentStrength ?? b.sourcePresentStrength ?? b.presentStrength ?? 0) - Number(a.finalValue ?? a.intrinsicPresentStrength ?? a.sourcePresentStrength ?? a.presentStrength ?? 0))
    .slice(0, 12);
  const profileRows = view.rows
    .filter((row) => row.presentRounds > 0)
    .slice()
    .sort((a, b) => Number(b.balanceValue ?? b.intrinsicPresentStrength ?? b.sourcePresentStrength ?? b.presentStrength ?? 0) - Number(a.balanceValue ?? a.intrinsicPresentStrength ?? a.sourcePresentStrength ?? a.presentStrength ?? 0))
    .slice(0, 12);
  const typeRows = view.typeSummaries;
  return (
    <ChartGrid>
      <CompactCard title="平衡强度 Top 12">
        <SimpleBarChart
          rows={topRows.map((row) => ({
            label: row.name || row.assetId,
            value: Number(row.finalValue ?? row.intrinsicPresentStrength ?? row.sourcePresentStrength ?? row.presentStrength ?? 0),
          }))}
          color="#1677ff"
        />
      </CompactCard>
      <CompactCard title="观测边际 Top 12">
        <SimpleBarChart
          rows={profileRows.map((row) => ({
            label: row.name || row.assetId,
            value: Number(row.balanceValue ?? row.intrinsicPresentStrength ?? row.sourcePresentStrength ?? row.presentStrength ?? 0),
          }))}
          color="#13c2c2"
        />
      </CompactCard>
      <CompactCard title="类型品质平均平衡强度">
        <SimpleBarChart
          rows={typeRows.map((row) => ({
            label: `${row.typeName}-${row.rarityName}`,
            value: row.averageGlobalStrength,
          }))}
          color="#722ed1"
        />
      </CompactCard>
    </ChartGrid>
  );
}

function SimpleBarChart({
  rows,
  color,
}: {
  rows: Array<{ label: string; value: number }>;
  color: string;
}) {
  const max = Math.max(0, ...rows.map((row) => Math.abs(row.value)));
  if (!rows.length) return <Text type="secondary">暂无数据</Text>;
  return (
    <BarChartWrap>
      {rows.map((row) => (
        <BarRow key={row.label}>
          <BarLabel title={row.label}>{row.label}</BarLabel>
          <BarTrack>
            <BarFill
              $color={color}
              $width={max ? Math.max(2, Math.abs(row.value) / max * 100) : 0}
            />
          </BarTrack>
          <Text>{row.value.toFixed(2)}</Text>
        </BarRow>
      ))}
    </BarChartWrap>
  );
}

function StatsTable({
  result,
  view,
  loading,
  onExport,
}: {
  result?: PropSimulationResult;
  view: SimulationView;
  loading?: boolean;
  onExport: () => void;
}) {
  const filteredRows = view.rows;
  const columns: any[] = [
    { title: "名称", dataIndex: "name", width: 130, fixed: "left" },
    { title: "类型", dataIndex: "typeName", width: 90 },
    { title: "品质", dataIndex: "rarityName", width: 90 },
    {
      title: "标签",
      dataIndex: "tagNames",
      width: 150,
      render: (values: string[]) => values?.map((item) => <Tag key={item}>{item}</Tag>),
    },
    { title: "平衡强度", dataIndex: "finalValue", width: 110, sorter: numericSorter("finalValue") },
    { title: "计算快照", dataIndex: "actualCalculatedStrength", width: 110, sorter: numericSorter("actualCalculatedStrength") },
    {
      title: "保存系数",
      dataIndex: "strengthCoefficient",
      width: 100,
      sorter: numericSorter("strengthCoefficient"),
      render: (value: number | null | undefined) => value === null || value === undefined ? "-" : `x${formatStatNumber(value)}`,
    },
    { title: "预期强度", dataIndex: "expectedValue", width: 110, sorter: numericSorter("expectedValue") },
    { title: "偏移", dataIndex: "deviation", width: 90, sorter: numericSorter("deviation") },
    { title: "置信区间", key: "confidence", width: 150, render: (_: unknown, row: PropSimulationStatRow) => `${formatStatNumber(row.confidenceLow)} ~ ${formatStatNumber(row.confidenceHigh)}` },
    { title: "观测边际", dataIndex: "balanceValue", width: 110, sorter: numericSorter("balanceValue") },
    { title: "触发强度", dataIndex: "triggerValue", width: 110, sorter: numericSorter("triggerValue") },
    { title: "池价值", dataIndex: "poolValue", width: 90, sorter: numericSorter("poolValue") },
    {
      title: "直接魔力",
      key: "directMana",
      width: 100,
      sorter: (a: PropSimulationStatRow, b: PropSimulationStatRow) => Number(a.components?.directMana || 0) - Number(b.components?.directMana || 0),
      render: (_: unknown, row: PropSimulationStatRow) => formatStatNumber(row.components?.directMana || 0),
    },
    {
      title: "状态差分",
      key: "stateDelta",
      width: 100,
      sorter: (a: PropSimulationStatRow, b: PropSimulationStatRow) => Number(a.components?.stateDelta || 0) - Number(b.components?.stateDelta || 0),
      render: (_: unknown, row: PropSimulationStatRow) => formatStatNumber(row.components?.stateDelta || 0),
    },
    {
      title: "成长差分",
      key: "growDelta",
      width: 100,
      sorter: (a: PropSimulationStatRow, b: PropSimulationStatRow) => Number(a.components?.growDelta || 0) - Number(b.components?.growDelta || 0),
      render: (_: unknown, row: PropSimulationStatRow) => formatStatNumber(row.components?.growDelta || 0),
    },
    {
      title: "生成差分",
      key: "spawnDelta",
      width: 100,
      sorter: (a: PropSimulationStatRow, b: PropSimulationStatRow) => Number(a.components?.spawnDelta || 0) - Number(b.components?.spawnDelta || 0),
      render: (_: unknown, row: PropSimulationStatRow) => formatStatNumber(row.components?.spawnDelta || 0),
    },
    {
      title: "消除差分",
      key: "eliminateDelta",
      width: 100,
      sorter: (a: PropSimulationStatRow, b: PropSimulationStatRow) => Number(a.components?.eliminateDelta || 0) - Number(b.components?.eliminateDelta || 0),
      render: (_: unknown, row: PropSimulationStatRow) => formatStatNumber(row.components?.eliminateDelta || 0),
    },
    {
      title: "转化差分",
      key: "transformDelta",
      width: 100,
      sorter: (a: PropSimulationStatRow, b: PropSimulationStatRow) => Number(a.components?.transformDelta || 0) - Number(b.components?.transformDelta || 0),
      render: (_: unknown, row: PropSimulationStatRow) => formatStatNumber(row.components?.transformDelta || 0),
    },
    { title: "自身贡献", dataIndex: "intrinsicContribution", width: 110, sorter: numericSorter("intrinsicContribution") },
    { title: "自身登场", dataIndex: "intrinsicPresentStrength", width: 110, sorter: numericSorter("intrinsicPresentStrength") },
    { title: "承接贡献", dataIndex: "bodyContribution", width: 110, sorter: numericSorter("bodyContribution") },
    { title: "承接全局", dataIndex: "bodyGlobalStrength", width: 110, sorter: numericSorter("bodyGlobalStrength") },
    { title: "承接登场", dataIndex: "bodyPresentStrength", width: 110, sorter: numericSorter("bodyPresentStrength") },
    { title: "来源贡献", dataIndex: "sourceContribution", width: 110, sorter: numericSorter("sourceContribution") },
    { title: "来源登场", dataIndex: "sourcePresentStrength", width: 110, sorter: numericSorter("sourcePresentStrength") },
    { title: "登场轮数", dataIndex: "presentRounds", width: 110, sorter: numericSorter("presentRounds") },
    { title: "使用次数", dataIndex: "uses", width: 100, sorter: numericSorter("uses") },
  ];
  return (
    <TableCard
      title={
        <Flex justify="space-between" align="center">
          <Space>
            <Text strong>数据统计表</Text>
            <Text type="secondary">{filteredRows.length} 项</Text>
          </Space>
          <Button icon={<DownloadOutlined />} disabled={!result} onClick={onExport}>
            导出CSV
          </Button>
        </Flex>
      }
    >
      <Table<PropSimulationStatRow>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={filteredRows}
        loading={loading}
        pagination={false}
        scroll={{ x: 2700 }}
      />
    </TableCard>
  );
}

function HistoryTable({
  histories,
  onView,
  onDelete,
  onExport,
}: {
  histories: PropSimulationHistory[];
  onView: (history: PropSimulationHistory) => void;
  onDelete: (history: PropSimulationHistory) => Promise<void>;
  onExport: (history: PropSimulationHistory) => void;
}) {
  const columns: any[] = [
    {
      title: "时间",
      dataIndex: "createdAt",
      width: 120,
      render: (value: number) => new Date(value).toLocaleString(),
    },
    {
      title: "局数",
      dataIndex: ["summary", "games"],
      width: 70,
      render: (value: number) => value?.toLocaleString(),
    },
    {
      title: "每局",
      dataIndex: ["summary", "averageMagicPerGame"],
      width: 70,
      render: (value: number) => Number(value ?? 0).toFixed(2),
    },
    {
      title: "操作",
      width: 90,
      render: (_: unknown, record: PropSimulationHistory) => (
        <Space size={2}>
          <Button icon={<EyeOutlined />} size="small" onClick={() => onView(record)} />
          <Button icon={<DownloadOutlined />} size="small" onClick={() => onExport(record)} />
          <Popconfirm title="删除这条模拟历史？" onConfirm={() => onDelete(record)}>
            <Button danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];
  return (
    <CompactCard title="模拟历史">
      <Table<PropSimulationHistory>
        rowKey="_id"
        size="small"
        columns={columns}
        dataSource={histories}
        pagination={false}
      />
    </CompactCard>
  );
}

function buildSimulationFilterOptions(
  result: PropSimulationResult | undefined,
  selectedCommission: string,
  tags: any[],
  types: any[],
  rarities: any[],
  profiles: PropSimulationIaaProfile[],
) {
  const commissionOptions = [
    { value: "all", label: "全部委托" },
    ...PROP_SIMULATION_COMMISSION_TURNS.map((_, index) => ({
      value: String(index + 1),
      label: `第${index + 1}次委托`,
    })),
  ];
  const roundOptions = [
    { value: "all", label: "全部轮次" },
    ...getRoundIndexes(result, selectedCommission).map((round) => ({
      value: String(round),
      label: `第${round}轮`,
    })),
  ];
  const systemOptions = [
    { value: "all", label: "全部体系" },
    ...tags
      .map((tag) => ({
        value: String(tag.value),
        label: String(tag.label ?? tag.name ?? tag.value),
      }))
      .sort((a, b) => Number(a.value) - Number(b.value)),
  ];
  const profileOptions = [
    { value: "all", label: "全部付费分层" },
    ...profiles.map((profile) => ({
      value: profile.key,
      label: profile.name,
    })),
  ];
  const typeOptions = [
    { value: "all", label: "全部类型" },
    ...types.map((type) => ({
      value: String(type.value),
      label: String(type.label ?? type.name ?? type.value),
    })),
  ];
  const rarityOptions = [
    { value: "all", label: "全部品质" },
    ...rarities.map((rarity) => ({
      value: String(rarity.value),
      label: String(rarity.label ?? rarity.name ?? rarity.value),
    })),
  ];
  return {
    commissionOptions,
    roundOptions,
    systemOptions,
    profileOptions,
    typeOptions,
    rarityOptions,
  };
}

function getRoundIndexes(
  result: PropSimulationResult | undefined,
  selectedCommission: string,
) {
  const roundScopes = (result?.statScopes ?? [])
    .filter((scope) => scope.type === "round")
    .map((scope) => ({
      round: Number(scope.meta?.roundIndex ?? scope.key.replace("round:", "")),
      commission: Number(scope.meta?.commissionIndex ?? 0),
    }))
    .filter((item) => Number.isFinite(item.round) && item.round > 0);
  if (roundScopes.length) {
    return roundScopes
      .filter((item) => selectedCommission === "all" || item.commission === Number(selectedCommission))
      .map((item) => item.round)
      .sort((a, b) => a - b);
  }

  const selected = Number(selectedCommission);
  let start = 1;
  let end = PROP_SIMULATION_COMMISSION_TURNS.reduce((sum, value) => sum + value, 0);
  if (Number.isFinite(selected) && selected > 0) {
    start = PROP_SIMULATION_COMMISSION_TURNS
      .slice(0, selected - 1)
      .reduce((sum, value) => sum + value, 0) + 1;
    end = start + (PROP_SIMULATION_COMMISSION_TURNS[selected - 1] ?? 0) - 1;
  }
  return Array.from({ length: Math.max(0, end - start + 1) }, (_, index) => start + index);
}

function buildSimulationView(
  result: PropSimulationResult | undefined,
  filters: SimulationFilterState,
  tags: any[],
  types: any[],
  rarities: any[],
  profiles: PropSimulationIaaProfile[],
): SimulationView {
  if (!result) {
    return {
      rows: [],
      summary: undefined,
      typeSummaries: [],
      label: "全部数据",
    };
  }

  const scope = findSimulationScope(result, filters);
  const rows = filterRowsByFields(scope?.rows ?? result.rows, filters);
  const summary = rebuildSummaryForRows(rows, scope?.summary ?? result.summary);
  return {
    rows,
    summary,
    typeSummaries: buildTypeSummariesFromRows(rows),
    label: buildSimulationViewLabel(filters, tags, types, rarities, profiles),
  };
}

function findSimulationScope(result: PropSimulationResult, filters: SimulationFilterState) {
  const scopes = result.statScopes ?? [];
  const matchedFilterScope = scopes.find((scope) => {
    if (scope.type !== "filter") return false;
    if (filters.round !== "all") {
      return (
        String(scope.meta?.roundIndex ?? "") === filters.round &&
        (filters.profile === "all" || String(scope.meta?.profileKey ?? "") === filters.profile)
      );
    }
    if (filters.commission !== "all") {
      return (
        String(scope.meta?.commissionIndex ?? "") === filters.commission &&
        (filters.profile === "all" || String(scope.meta?.profileKey ?? "") === filters.profile)
      );
    }
    return false;
  });
  if (matchedFilterScope) return matchedFilterScope;

  if (filters.round !== "all") {
    return scopes.find((scope) => scope.type === "round" && String(scope.meta?.roundIndex ?? scope.key.replace("round:", "")) === filters.round);
  }
  if (filters.commission !== "all") {
    return scopes.find((scope) => scope.type === "commission" && String(scope.meta?.commissionIndex ?? scope.key.replace("commission:", "")) === filters.commission);
  }
  if (filters.profile !== "all") {
    return scopes.find((scope) => scope.type === "profile" && String(scope.meta?.profileKey ?? scope.key.replace("profile:", "")) === filters.profile);
  }
  return undefined;
}

function filterRowsByFields(rows: PropSimulationStatRow[], filters: SimulationFilterState) {
  return rows.filter((row) => {
    if (filters.system !== "all") {
      const tagValue = Number(filters.system);
      if (!row.tags.some((tag) => Number(tag) === tagValue)) return false;
    }
    if (filters.type !== "all" && String(row.type) !== filters.type) return false;
    if (filters.rarity !== "all" && String(row.rarity) !== filters.rarity) return false;
    return true;
  });
}

function buildSimulationViewLabel(
  filters: SimulationFilterState,
  tags: any[],
  types: any[],
  rarities: any[],
  profiles: PropSimulationIaaProfile[],
) {
  const parts: string[] = [];
  if (filters.commission !== "all") parts.push(`第${filters.commission}次委托`);
  if (filters.round !== "all") parts.push(`第${filters.round}轮`);
  if (filters.system !== "all") {
    const tag = tags.find((item) => String(item.value) === filters.system);
    parts.push(String(tag?.label ?? tag?.name ?? filters.system));
  }
  if (filters.type !== "all") {
    const type = types.find((item) => String(item.value) === filters.type);
    parts.push(String(type?.label ?? type?.name ?? filters.type));
  }
  if (filters.rarity !== "all") {
    const rarity = rarities.find((item) => String(item.value) === filters.rarity);
    parts.push(String(rarity?.label ?? rarity?.name ?? filters.rarity));
  }
  if (filters.profile !== "all") {
    const profile = profiles.find((item) => item.key === filters.profile);
    parts.push(profile?.name ?? filters.profile);
  }
  return parts.length ? parts.join(" / ") : "全部数据";
}

function rebuildSummaryForRows(
  rows: PropSimulationStatRow[],
  summary: PropSimulationRunSummary,
): PropSimulationRunSummary {
  const totalMagic = rows.reduce((sum, row) => sum + Number(row.contribution || 0), 0);
  return {
    ...summary,
    totalMagic: roundNumber(totalMagic, 4),
    averageMagicPerGame: roundNumber(totalMagic / Math.max(1, summary.games), 4),
    averageMagicPerTurn: roundNumber(totalMagic / Math.max(1, summary.totalTurns), 4),
    propCount: rows.length,
    positivePropCount: rows.filter((row) => Number(row.finalValue ?? row.intrinsicPresentStrength ?? row.sourcePresentStrength ?? row.presentStrength ?? 0) > 0).length,
    negativePropCount: rows.filter((row) => Number(row.finalValue ?? row.intrinsicPresentStrength ?? row.sourcePresentStrength ?? row.presentStrength ?? 0) < 0).length,
    presentPropCount: rows.filter((row) => row.presentRounds > 0).length,
    neverPresentPropCount: rows.filter((row) => row.presentRounds <= 0).length,
  };
}

function buildTypeSummariesFromRows(rows: PropSimulationStatRow[]): PropSimulationTypeSummary[] {
  const map = new Map<string, {
    type: number;
    typeName: string;
    rarity: number;
    rarityName: string;
    count: number;
    globalStrengthSum: number;
    presentStrengthSum: number;
  }>();
  rows.forEach((row) => {
    const key = `${row.type}:${row.rarity}`;
    if (!map.has(key)) {
      map.set(key, {
        type: row.type,
        typeName: row.typeName,
        rarity: row.rarity,
        rarityName: row.rarityName,
        count: 0,
        globalStrengthSum: 0,
        presentStrengthSum: 0,
      });
    }
    const item = map.get(key)!;
    item.count += 1;
    item.globalStrengthSum += Number(row.finalValue ?? row.intrinsicPresentStrength ?? row.sourcePresentStrength ?? row.presentStrength ?? 0);
    item.presentStrengthSum += Number(row.balanceValue ?? row.intrinsicPresentStrength ?? row.sourcePresentStrength ?? row.presentStrength ?? 0);
  });
  return Array.from(map.values())
    .map((item) => ({
      type: item.type,
      typeName: item.typeName,
      rarity: item.rarity,
      rarityName: item.rarityName,
      count: item.count,
      averageGlobalStrength: roundNumber(item.globalStrengthSum / Math.max(1, item.count), 4),
      averagePresentStrength: roundNumber(item.presentStrengthSum / Math.max(1, item.count), 4),
    }))
    .sort((a, b) => a.type - b.type || a.rarity - b.rarity);
}

function roundNumber(value: number, precision = 4) {
  if (!Number.isFinite(Number(value))) return 0;
  const unit = Math.pow(10, precision);
  return Math.round(Number(value) * unit) / unit;
}

function formatStatNumber(value: unknown) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "0";
  return String(Number(number.toFixed(2)));
}

function numericSorter(key: keyof PropSimulationStatRow) {
  return (a: PropSimulationStatRow, b: PropSimulationStatRow) =>
    Number(a[key] ?? 0) - Number(b[key] ?? 0);
}

function createRandomSeed() {
  return Math.floor(Math.random() * 2147483646) + 1;
}

function usePropGraphData() {
  const context = useContext(PropGraphDataContext);
  if (!context) {
    throw new Error("usePropGraphData must be used within PropGraphDataContext");
  }
  return context;
}
